import { request, resourceUrl } from './http';
import type {
  Alert, ApiKey, ApiKeyScope, AssetCondition, AssetEvent, AssetInput, AssetWithPosition, Asset, LayoutPayload, LiveItem, PickList, PlanDetail, PlanSummary, SlotOverview,
  ClientOverview, Site, Tenant, TenantDashboard, TenantRole, TenantSummary, TrailPoint, User, ZoneDwellRow, ZoneVisit,
} from './types';

type Ok = { ok: true };

/** Session endpoints (not tied to a tenant). */
export const authApi = {
  login: (email: string, password: string) => request<{ user: User; expiresAt: string }>('/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => request<Ok>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) => request<Ok>('/auth/password', { method: 'POST', body: { currentPassword, newPassword } }),
};

/** Platform administration. */
export const tenantsApi = {
  list: () => request<TenantSummary[]>('/tenants'),
  get: (id: string) => request<Tenant>(`/tenants/${id}`),
  create: (body: { name: string; firstAdmin?: { name: string; email: string; password: string } }) => request<Tenant>('/tenants', { method: 'POST', body }),
  rename: (id: string, name: string) => request<Ok>(`/tenants/${id}`, { method: 'PATCH', body: { name } }),
  remove: (id: string) => request<Ok>(`/tenants/${id}`, { method: 'DELETE' }),
  overview: () => request<{ generatedAt: string; clients: ClientOverview[] }>('/admin/overview'),
};

export interface AssetListParams {
  search?: string;
  type?: string;
  limit?: number;
  offset?: number;
}

/**
 * Endpoints scoped to one tenant. `tenantId` is sent only when the caller is a
 * platform administrator acting inside a client space; regular users are
 * always confined to their own tenant by the server.
 */
export function createTenantApi(tenantId: string | null) {
  const r = <T>(path: string, options: Parameters<typeof request>[1] = {}) => request<T>(path, { ...options, tenantId });
  const query = (params: Record<string, string | number | undefined>) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== '') search.set(key, String(value));
    const s = search.toString();
    return s ? `?${s}` : '';
  };

  return {
    tenantId,
    users: {
      list: () => r<User[]>('/users'),
      create: (body: { name: string; email: string; password: string; role: TenantRole }) => r<User>('/users', { method: 'POST', body }),
      update: (id: string, body: { name?: string; role?: TenantRole; disabled?: boolean; password?: string }) => r<Ok>(`/users/${id}`, { method: 'PATCH', body }),
      remove: (id: string) => r<Ok>(`/users/${id}`, { method: 'DELETE' }),
    },
    sites: {
      list: () => r<Site[]>('/sites'),
      create: (body: { name: string; address?: string | null }) => r<Site>('/sites', { method: 'POST', body }),
      update: (id: string, body: { name: string; address?: string | null }) => r<Ok>(`/sites/${id}`, { method: 'PUT', body }),
      remove: (id: string) => r<Ok>(`/sites/${id}`, { method: 'DELETE' }),
    },
    plans: {
      list: () => r<PlanSummary[]>('/plans'),
      get: (id: string) => r<PlanDetail>(`/plans/${id}`),
      create: (body: { name: string; siteId?: string | null; level?: number }) => r<PlanSummary>('/plans', { method: 'POST', body }),
      update: (id: string, body: { name: string; siteId?: string | null; level?: number }) => r<Ok>(`/plans/${id}`, { method: 'PATCH', body }),
      remove: (id: string) => r<Ok>(`/plans/${id}`, { method: 'DELETE' }),
      uploadImage: (id: string, file: Blob, filename: string) => {
        const form = new FormData();
        form.append('file', file, filename);
        return r<Ok>(`/plans/${id}/image`, { method: 'PUT', body: form });
      },
      imageUrl: (id: string, version?: string) => resourceUrl(`/plans/${id}/image`, tenantId) + (version ? `${tenantId ? '&' : '?'}v=${encodeURIComponent(version)}` : ''),
      saveLayout: (id: string, body: LayoutPayload) => r<Ok>(`/plans/${id}/layout`, { method: 'PUT', body }),
      setSimulation: (id: string, enabled: boolean) => r<Ok>(`/plans/${id}/simulation`, { method: 'PUT', body: { enabled } }),
      positions: (id: string) => r<LiveItem[]>(`/plans/${id}/positions`),
      liveUrl: (id: string) => resourceUrl(`/plans/${id}/live`, tenantId),
    },
    assets: {
      list: (params: AssetListParams = {}) => r<{ items: AssetWithPosition[]; total: number }>(`/assets${query({ ...params })}`),
      get: (id: string) => r<AssetWithPosition>(`/assets/${id}`),
      create: (body: AssetInput) => r<Asset>('/assets', { method: 'POST', body }),
      bulkCreate: (items: AssetInput[]) => r<{ created: number }>('/assets/bulk', { method: 'POST', body: { items } }),
      update: (id: string, body: AssetInput) => r<Asset>(`/assets/${id}`, { method: 'PUT', body }),
      remove: (id: string) => r<Ok>(`/assets/${id}`, { method: 'DELETE' }),
      lookup: (code: string) => r<AssetWithPosition>(`/assets/lookup${query({ code })}`),
      setTags: (id: string, body: { tagId?: string | null; rfidEpc?: string | null; force?: boolean }) => r<AssetWithPosition>(`/assets/${id}/tags`, { method: 'PUT', body }),
      place: (id: string, body: { planId: string; x: number; y: number }) => r<LiveItem>(`/assets/${id}/position`, { method: 'PUT', body }),
      unplace: (id: string) => r<Ok>(`/assets/${id}/position`, { method: 'DELETE' }),
      store: (id: string, slotId: string | null) => r<Asset>(`/assets/${id}/storage`, { method: 'PUT', body: { slotId } }),
      setCondition: (id: string, condition: AssetCondition, note?: string | null) => r<Asset>(`/assets/${id}/condition`, { method: 'PUT', body: { condition, note: note ?? null } }),
      events: (id: string) => r<AssetEvent[]>(`/assets/${id}/events`),
      trail: (id: string, minutes: number) => r<TrailPoint[]>(`/assets/${id}/trail${query({ minutes })}`),
      visits: (id: string, days: number) => r<ZoneVisit[]>(`/assets/${id}/visits${query({ days })}`),
    },
    dashboard: {
      get: () => r<TenantDashboard>('/dashboard'),
    },
    storage: {
      slots: (planId?: string) => r<SlotOverview[]>(`/storage/slots${query({ planId })}`),
      lookup: (code: string) => r<SlotOverview>(`/storage/slots/lookup${query({ code })}`),
      press: (slotId: string, action: 'picked' | 'problem' | 'maintenance' | 'ok') => r<Ok>(`/storage/slots/${slotId}/press`, { method: 'POST', body: { action } }),
    },
    picks: {
      list: () => r<PickList[]>('/pick-lists'),
      get: (id: string) => r<PickList>(`/pick-lists/${id}`),
      create: (body: { name: string; assetIds: string[] }) => r<PickList>('/pick-lists', { method: 'POST', body }),
      pick: (id: string, assetId: string) => r<PickList>(`/pick-lists/${id}/items/${assetId}/pick`, { method: 'POST' }),
      cancel: (id: string) => r<Ok>(`/pick-lists/${id}/cancel`, { method: 'POST' }),
    },
    analytics: {
      zoneDwell: (params: { planId?: string; from?: string; to?: string }) =>
        r<{ from: string; to: string; rows: ZoneDwellRow[] }>(`/analytics/zone-dwell${query(params)}`),
      zoneDwellCsvUrl: (params: { planId?: string; from?: string; to?: string }) =>
        resourceUrl('/analytics/zone-dwell.csv', tenantId) + query(params).replace(/^\?/, tenantId ? '&' : '?'),
    },
    alerts: {
      list: () => r<Alert[]>('/alerts'),
    },
    apiKeys: {
      list: () => r<ApiKey[]>('/api-keys'),
      create: (body: { name: string; scopes: ApiKeyScope[] }) => r<{ key: ApiKey; secret: string }>('/api-keys', { method: 'POST', body }),
      revoke: (id: string) => r<Ok>(`/api-keys/${id}`, { method: 'DELETE' }),
    },
  };
}

export type TenantApi = ReturnType<typeof createTenantApi>;
